import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Tag, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ManageCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", icon: "✨", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/categories");
      setItems(r.data.items);
    } catch (e) {
      toast.error("Failed to load categories");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", icon: "✨", description: "" }); setOpen(true); };
  const openEdit = (cat) => { setEditing(cat); setForm({ name: cat.name, icon: cat.icon || "✨", description: cat.description || "" }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Category created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const del = async (cat) => {
    if (cat.spa_count > 0) {
      return toast.error(`Cannot delete: ${cat.spa_count} spa(s) still use this category`);
    }
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl">Manage Categories</h2>
          <p className="text-sm text-[#4B5563] mt-1">{items.length} categor{items.length === 1 ? "y" : "ies"} · used by spas across the platform</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="admin-add-category-button" className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2">
              <Plus size={14} /> Add Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Hot Stone Therapy" data-testid="category-name-input" />
              </div>
              <div>
                <Label className="text-xs">Icon (emoji)</Label>
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="✨" maxLength={3} data-testid="category-icon-input" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" data-testid="category-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-[#111827] text-white" data-testid="category-save-button">
                {editing ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#4B5563]">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} data-testid={`category-card-${c.name.toLowerCase().replace(/\s/g, '-')}`} className="group rounded-2xl bg-white border border-[#E7DDCF] p-5 hover:shadow-[0_12px_30px_rgba(17,24,39,0.08)] transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#F5EFE6] flex items-center justify-center text-2xl">{c.icon || "✨"}</div>
                  <div>
                    <div className="font-heading text-lg leading-tight">{c.name}</div>
                    <div className="text-xs text-[#4B5563] mt-0.5">{c.spa_count} spa{c.spa_count === 1 ? "" : "s"} listed</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-[#F5EFE6]" data-testid={`category-edit-${c.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => del(c)}
                    disabled={c.spa_count > 0}
                    className={`p-2 rounded-lg ${c.spa_count > 0 ? "text-[#4B5563]/40 cursor-not-allowed" : "text-[#B42318] hover:bg-red-50"}`}
                    data-testid={`category-delete-${c.name.toLowerCase().replace(/\s/g, '-')}`}
                    title={c.spa_count > 0 ? "Reassign spas before deleting" : "Delete category"}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {c.description && <p className="text-xs text-[#4B5563] mt-3 line-clamp-2">{c.description}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] p-4 text-xs text-[#4B5563]">
        <strong className="text-[#111827]">Tip:</strong> Renaming a category will automatically update all spas that use it. Deletion is blocked while spas reference the category &mdash; reassign them first via the Spas page.
      </div>
    </div>
  );
}
